import { tracking } from '@navio/observability';

import { commonTrackingEvents, type ProposalAnalyticsProps } from './events';
import { pickDefined } from './pickDefined';
import { isClaimed, markClaimed, sessionOnceKey } from './sessionOnce';

/**
 * Proposal context as session super-props, so signals that carry no props of
 * their own — OpenPanel `screen_view`, the customer Mixpanel path events — can
 * still be segmented by brand, department and customer type.
 *
 * Every key is `proposal`-prefixed on purpose: the booking flow registers its own
 * `brand` / `locationType`, and the proposal card can hand a customer straight
 * into that flow. Sharing key names there would let one flow overwrite the
 * other's context, which is the "never sync between sources" trap.
 */
const PROPOSAL_SUPER_PROP_KEYS = [
  'proposalId',
  'proposalType',
  'proposalIsRecall',
  'proposalLocationType',
  'proposalCustomerType',
  'proposalBrand',
  'proposalServiceDepartmentId',
  'proposalServiceDepartmentName',
  'proposalUsesSuggestedTimes'
];

export function registerProposalContext(props: ProposalAnalyticsProps) {
  tracking.register(
    pickDefined({
      proposalId: props.proposalId,
      proposalType: props.proposalType,
      proposalIsRecall: props.isRecall,
      proposalLocationType: props.locationType,
      proposalCustomerType: props.customerType,
      proposalBrand: props.brand,
      proposalServiceDepartmentId: props.serviceDepartmentId,
      proposalServiceDepartmentName: props.serviceDepartmentName,
      proposalUsesSuggestedTimes: props.usesSuggestedTimes
    })
  );
}

/** Leaving the flow must not tag later booking-flow events as proposal traffic. */
export function clearProposalContext() {
  tracking.unregister(PROPOSAL_SUPER_PROP_KEYS);
}

/**
 * Funnel entry, once per proposal per analytics session. Claimed per proposal id
 * rather than globally: one customer can hold proposals for several cars, and
 * each is its own funnel. Re-entering the same one after a back navigation or a
 * mid-flow login is not a second start.
 */
export function trackProposalStarted(props: ProposalAnalyticsProps) {
  const key = sessionOnceKey(`proposal-started:${props.proposalId}`);
  if (isClaimed(key)) {
    return;
  }
  tracking.track(commonTrackingEvents.proposalStarted, props);
  markClaimed(key);
}
